import Dexie from "dexie";
import { StorageController } from "src/state/StorageBloc/StorageBloc";
import type {
  CachedConversation,
  CachedMessage,
  CachedPrompt,
  SyncMeta
} from "./dbTypes";

const DB_OWNER_KEY = "leapchat-db-owner";
let ownershipReady: Promise<void> | null = null;

export const ensureOwnership = (): Promise<void> => {
  if (ownershipReady) {
    return ownershipReady;
  }
  ownershipReady = (async () => {
    const { activeUserId } = StorageController;
    if (!activeUserId) {
      await clearDb();
      return;
    }
    const storedOwner = localStorage.getItem(DB_OWNER_KEY);
    if (storedOwner === activeUserId) {
      return;
    }
    await clearDb();
    localStorage.setItem(DB_OWNER_KEY, activeUserId);
  })();
  return ownershipReady;
};

export const invalidateOwnership = (): void => {
  ownershipReady = null;
};

class LeapChatDb extends Dexie {
  conversations!: Dexie.Table<CachedConversation, string>;
  messages!: Dexie.Table<CachedMessage, string>;
  prompts!: Dexie.Table<CachedPrompt, string>;
  syncMeta!: Dexie.Table<SyncMeta, string>;

  constructor() {
    super("LeapChatDb");

    this.version(1).stores({
      conversations: "id, updatedAt",
      messages: "id, conversationId, createdAt, [conversationId+createdAt]",
      syncMeta: "key"
    });

    this.version(2).stores({
      prompts: "id, conversationId, messageId"
    });
  }
}

let instance: LeapChatDb | null = null;

export const getDb = (): LeapChatDb => {
  if (!instance) {
    instance = new LeapChatDb();
  }
  return instance;
};

export const clearDb = async (): Promise<void> => {
  const db = getDb();
  await db.transaction(
    "rw",
    [db.conversations, db.messages, db.prompts, db.syncMeta],
    async () => {
      await db.conversations.clear();
      await db.messages.clear();
      await db.prompts.clear();
      await db.syncMeta.clear();
    }
  );
  localStorage.removeItem(DB_OWNER_KEY);
};
